import {
  signInWithEmailAndPassword,
  signOut,
  Auth,
  GoogleAuthProvider,
  signInWithRedirect,
  connectAuthEmulator,
  setPersistence,
  UserCredential,
  getAuth,
  browserLocalPersistence,
  signInWithCustomToken,
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';

import { ConfigService } from './configService';
import { ApiService } from './apiService';
import { RedirectService } from './redirectService';
import { getConfig } from '../config';
import { LoginToAGWResponse } from '../types/common';
import { ToastService } from './toastService';
import { Logger } from '../utils/logger';
import { isTopApp } from '../utils/isTopApp';
import { CustomTokenResponse } from '../types/CustomTokenResponse';
import { AuthError } from '../errors/AuthError';
import { MessageService } from './messageService';

export class AuthService {
  private static instance: AuthService;
  private readonly apiService: ApiService;
  private readonly configService: ConfigService;
  private readonly redirectService: RedirectService;
  private readonly toastService: ToastService;
  private readonly useEmulator: boolean = JSON.parse(getConfig().FIREBASE_USE_AUTH_EMULATOR);
  private messageService!: MessageService;
  private auth?: Auth;
  private emulatorConnected: boolean = false;
  private prevIdToken: string | null = null;

  public loggedUser: LoginToAGWResponse | null = null;

  private initializedPromise?: Promise<boolean>;

  private constructor() {
    this.configService = ConfigService.getInstance();
    this.apiService = ApiService.getInstance();
    this.redirectService = RedirectService.getInstance();
    this.toastService = ToastService.getInstance();
  }

  // Firebase initialization is started lazily on first access instead of in the
  // constructor, so the singleton is constructed synchronously (SonarCloud S7059).
  // The promise is memoized, so initialization still runs exactly once.
  public get initialized(): Promise<boolean> {
    this.initializedPromise ??= this.initializeFirebase();
    return this.initializedPromise;
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
      MessageService.initialize(AuthService.instance);
      AuthService.instance.messageService = MessageService.getInstance();
    }

    return AuthService.instance;
  }

  private get googleAuthProvider(): GoogleAuthProvider {
    const provider = new GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/calendar');
    return provider;
  }

  get firebaseAuth(): Auth {
    if (!this.auth) {
      throw new AuthError('Firebase Auth not initialized');
    }

    return this.auth;
  }

  public async handleLoginResponse(loginResult: UserCredential): Promise<void> {
    try {
      const idToken = await loginResult.user.getIdToken();
      await this.apiService.createCustomToken(idToken);

      this.loggedUser = await this.apiService.loginToAGW(idToken);
      await this.enrichWithTokenClaims();
      await this.verifyPermissions(this.loggedUser);

      if (isTopApp()) {
        this.redirectService.redirectToApp();
      } else {
        Logger.logDebug('Handle login called in iframe. Redirect action is skipped.');
      }
    } catch (error) {
      await this.handleError(error, { logoutOnError: true });
    }
  }

  public async loginWithPassword(email: string, password: string): Promise<void> {
    await this.ensureInitialized();
    try {
      const loginResult = await signInWithEmailAndPassword(this.firebaseAuth, email, password);
      await this.handleLoginResponse(loginResult);
    } catch (error) {
      await this.handleError(error, {
        notificationMessage: 'Invalid email or password',
      });
    }
  }

  public async loginWithGoogle(): Promise<void> {
    await this.ensureInitialized();
    await setPersistence(this.firebaseAuth, browserLocalPersistence);
    await signInWithRedirect(this.firebaseAuth, this.googleAuthProvider);
  }

  public async loginWithCustomToken(token: string): Promise<void> {
    await this.ensureInitialized();
    await setPersistence(this.firebaseAuth, browserLocalPersistence);
    await signInWithCustomToken(this.firebaseAuth, token);
  }

  public async impersonate(collaboratorUuid: string): Promise<void> {
    const idToken = await this.getIdToken();
    if (!idToken) {
      const error = new AuthError('No active session');
      await this.handleError(error, {
        notificationMessage: 'Impersonation failed',
      });
      return;
    }

    try {
      await this.apiService.impersonate(collaboratorUuid, idToken);

      // After impersonation, we need to refresh the token flow
      const customTokenResponse = await this.apiService.getCustomToken();
      if (!customTokenResponse.data) {
        throw new AuthError('Failed to get custom token after impersonation');
      }

      const userCredential = await signInWithCustomToken(this.firebaseAuth, customTokenResponse.data);
      const newIdToken = await userCredential.user.getIdToken();
      await this.refreshAGWLogin(newIdToken);
    } catch (error) {
      await this.handleError(error, {
        notificationMessage: 'Impersonation failed',
      });
    }
  }

  public async stopImpersonate(): Promise<void> {
    const idToken = await this.getIdToken();
    if (!idToken) {
      const error = new AuthError('No active session');
      await this.handleError(error, {
        notificationMessage: 'Stop impersonation failed',
      });
      return;
    }

    try {
      await this.apiService.stopImpersonate(idToken);

      // After stop impersonation, we need to refresh the token flow
      const customTokenResponse = await this.apiService.getCustomToken();
      if (!customTokenResponse.data) {
        throw new AuthError('Failed to get custom token after stop impersonation');
      }

      const userCredential = await signInWithCustomToken(this.firebaseAuth, customTokenResponse.data);
      const newIdToken = await userCredential.user.getIdToken();
      await this.refreshAGWLogin(newIdToken);
    } catch (error) {
      await this.handleError(error, {
        notificationMessage: 'Stop impersonation failed',
      });
    }
  }
  public async getCustomToken(): Promise<CustomTokenResponse> {
    return this.apiService.getCustomToken();
  }

  public async clearCustomTokenCookie(): Promise<void> {
    await this.apiService.clearCustomTokenCookie();
  }

  public async getIdToken(forceRefresh: boolean = false): Promise<string | null> {
    const idToken = (await this.firebaseAuth.currentUser?.getIdToken(forceRefresh)) ?? null;
    if (idToken !== this.prevIdToken) {
      Logger.logDebug('Id token updated');
      this.prevIdToken = idToken;
    }
    return idToken;
  }

  public async refreshAGWLogin(idToken: string): Promise<void> {
    try {
      this.loggedUser = await this.apiService.loginToAGW(idToken);
      await this.enrichWithTokenClaims();
    } catch (error) {
      await this.handleError(error, { logoutOnError: true });
    }
  }

  private async enrichWithTokenClaims(): Promise<void> {
    if (!this.loggedUser) return;
    const result = await this.firebaseAuth.currentUser?.getIdTokenResult();
    if (result) {
      this.loggedUser.isImpersonated = result.claims['isImpersonated'] === true;
    }
  }

  /**
   * Logs out the currently signed-in user from the application and Firebase authentication.
   *
   * @param {boolean} [eventFromExternalSource] - Optional flag indicating whether the logout event was triggered from an external source.
   * @return {Promise<void>} A promise that resolves when the logout process completes.
   */
  public async logout(options: { eventFromExternalSource?: boolean; clearCookie?: boolean } = {}): Promise<void> {
    await this.ensureInitialized();
    if (options.clearCookie) {
      await this.apiService.clearCustomTokenCookie();
    }
    this.loggedUser = null;
    await signOut(this.firebaseAuth);
    if (options.eventFromExternalSource) {
      this.messageService.sendLogoutMessage();
    }
    Logger.logInfo('Signed out successfully', this.firebaseAuth.currentUser);
  }

  private async initializeFirebase(): Promise<boolean> {
    try {
      const config = await this.configService.getConfig();

      initializeApp(config.firebase);
      this.auth = getAuth();
      this.auth.tenantId = config.firebase.tenantId;

      if (this.useEmulator && !this.emulatorConnected) {
        connectAuthEmulator(this.auth, getConfig().FIREBASE_EMULATOR, {
          disableWarnings: true,
        });
        this.emulatorConnected = true;
      }

      await this.auth.authStateReady();
      await this.getIdToken(true);
      return true;
    } catch (error) {
      await this.handleError(error);
      return false;
    }
  }

  private async handleError(
    error: unknown,
    {
      notificationMessage,
      logoutOnError,
    }: {
      notificationMessage?: string;
      logoutOnError?: boolean;
    } = {},
  ): Promise<void> {
    Logger.logError(error);
    const message = error instanceof Error ? error.message : String(error);
    this.toastService.showErrorToast(notificationMessage || message);
    if (logoutOnError) {
      await this.logout();
    }
    throw new AuthError(message, error);
  }

  private async verifyPermissions(agwResponse: LoginToAGWResponse): Promise<void> {
    if (!agwResponse.companyPermissions) {
      await this.logout();
      throw new AuthError('Company permissions not found');
    }

    const companyPermissions = agwResponse.companyPermissions.find(
      (cp) => cp.code === this.configService.getOrganizationCode(),
    );

    if (!companyPermissions) {
      await this.logout();
      throw new AuthError(`No permissions found for organization code "${this.configService.getOrganizationCode()}"`);
    }

    const appPermission = companyPermissions.permissions.find(
      (permission) => permission.name === `${this.configService.getActivePermissionPrefix()}_APP`,
    );

    if (!appPermission) {
      await this.logout();
      throw new AuthError(`No permissions found for subject "${this.configService.getActivePermissionPrefix()}_APP"`);
    }

    const hasLoginPermission = appPermission.actions.some(
      (action) => action.name === this.configService.loginPermission,
    );

    if (!hasLoginPermission) {
      await this.logout();
      throw new AuthError(
        `No permissions found for action "${this.configService.loginPermission}" in subject "${this.configService.getActivePermissionPrefix()}_APP"`,
      );
    }
  }

  private async ensureInitialized(): Promise<void> {
    const initialized = await this.initialized;
    if (!initialized) {
      throw new AuthError('Firebase not initialized');
    }
  }
}
